import React from 'react';
import './LessonPlayer.css';

// LessonPlayer — THE WHITEBOARD. 2026-08-12 (ARIA_VISUAL_DOCTRINE.md §3.2).
//
// Braeden: "go step by step by drawing a fram by fram video of an illistration
// talking thbrough and explaining." Approved form: whiteboard draw-on —
// strokes appear while the step's words sit under the board.
//
// The lesson arrives as steps with CUMULATIVE scenes (ariaLessonBuilder).
// Rendering step N draws the whole board; only the items that are NEW at
// step N animate on — everything older sits still, like real chalk.
//
// Controls: ▶/⏸ auto-play, ← →, replay, slower. Auto-advance paces itself on
// the sentence length so she never flips the board mid-thought.

const STEP_MS = (say, slow) => (1600 + String(say || '').length * 55) * (slow ? 1.7 : 1);

function Prim({ p, draw, delay }) {
  const cls = draw ? 'lp-draw' : '';
  const style = draw ? { animationDelay: `${delay}ms` } : undefined;
  const tstyle = draw ? { animationDelay: `${delay}ms` } : undefined;
  switch (p.t) {
    case 'text':
      return (
        <text x={p.x} y={p.y} fontSize={p.s} className={`lp-text ${draw ? 'lp-text-in' : ''}`} style={tstyle}>
          {p.text}
        </text>
      );
    case 'line':
      return <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} pathLength="1" className={`lp-stroke ${cls}`} style={style} />;
    case 'strike':
      return <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} pathLength="1" className={`lp-stroke lp-strike ${cls}`} style={style} />;
    case 'arrow':
      return (
        <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} pathLength="1"
          className={`lp-stroke ${cls}`} style={style} markerEnd="url(#lp-arrowhead)" />
      );
    case 'circle':
      return <circle cx={p.x} cy={p.y} r={p.r} pathLength="1" className={`lp-stroke lp-round ${cls}`} style={style} />;
    case 'box':
      return <rect x={p.x} y={p.y} width={p.w} height={p.h} rx="6" pathLength="1" className={`lp-stroke lp-round ${cls}`} style={style} />;
    default:
      return null;
  }
}

export default function LessonPlayer({ lesson }) {
  const steps = (lesson && Array.isArray(lesson.steps)) ? lesson.steps : [];
  const [i, setI] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const [slow, setSlow] = React.useState(false);
  // bump forces remount of the current step's new prims so replay re-draws
  const [bump, setBump] = React.useState(0);

  const step = steps[i];
  const prevLen = i > 0 ? steps[i - 1].scene.length : 0;

  React.useEffect(() => {
    if (!playing || !step || i >= steps.length - 1) return undefined;
    const t = setTimeout(() => setI((x) => Math.min(x + 1, steps.length - 1)), STEP_MS(step.say, slow));
    return () => clearTimeout(t);
  }, [i, playing, slow, bump, step, steps.length]);

  if (!steps.length) return null;

  const go = (n) => { setI(Math.max(0, Math.min(n, steps.length - 1))); setBump((b) => b + 1); };

  return (
    <div className="lp">
      <div className="lp-title">{lesson.title}</div>

      <svg viewBox="0 0 400 300" className="lp-board" role="img" aria-label={lesson.title}>
        <defs>
          <marker id="lp-arrowhead" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <path d="M1,1 L7,4 L1,7" className="lp-arrowhead" />
          </marker>
        </defs>
        {step.scene.map((p, k) => (
          <Prim
            key={`${i}-${bump}-${k}`}
            p={p}
            draw={k >= prevLen}
            delay={(k - prevLen) * (slow ? 420 : 240)}
          />
        ))}
      </svg>

      <div className="lp-say">{step.say}</div>

      <div className="lp-controls">
        <button type="button" onClick={() => go(0)} title="Replay from the start">↺</button>
        <button type="button" onClick={() => go(i - 1)} disabled={i === 0} title="Back">←</button>
        <button type="button" onClick={() => setPlaying((p) => !p)} title={playing ? 'Pause' : 'Play'}>
          {playing ? '⏸' : '▶'}
        </button>
        <button type="button" onClick={() => go(i + 1)} disabled={i >= steps.length - 1} title="Next step">→</button>
        <button type="button" className={slow ? 'is-on' : ''} onClick={() => setSlow((s) => !s)} title="Slow it down">
          slower
        </button>
        <span className="lp-count">{i + 1} / {steps.length}</span>
      </div>

      {lesson.source && <div className="lp-source">{lesson.source}</div>}
    </div>
  );
}
